import { useState } from "react";
import { Heart, Minus, Plus, Check } from "lucide-react";
import type { CatalogProduct } from "@/lib/catalog/products";
import { DEFAULT_SIZE, DEFAULT_SIZES, formatINR, type SizeVariant } from "@/lib/pdp/product-helpers";
import { addToCart } from "@/lib/cart/store";
import { priceForSize } from "@/lib/catalog/size-pricing";
import { formatPrice, getDiscountedPrice } from "@/lib/cart/pricing";
import { useShippingConfig } from "@/lib/admin/settings";
import { ProductPrice } from "@/components/common/ProductPrice";
import { toggleWishlist, useIsWishlisted } from "@/lib/wishlist/store";
import { useProductSizeStock } from "@/lib/inventory/client";
import { LOW_STOCK_THRESHOLD } from "@/lib/inventory/sizes";

export function PurchasePanel({ product }: { product: CatalogProduct }) {
  const [size, setSize] = useState<SizeVariant>(DEFAULT_SIZE);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const wishlisted = useIsWishlisted(product.id);
  const stock = useProductSizeStock(product.id);
  const shippingConfig = useShippingConfig();

  // Stock is read for the SELECTED bottle size only. When the catalog row has
  // no stock record yet (mock/demo products) purchasing stays enabled.
  const sizeStock = stock ? (stock[size.id]?.onHand ?? 0) : null;
  const outOfStock = sizeStock !== null && sizeStock <= 0;
  const lowStock =
    sizeStock !== null && sizeStock > 0 && sizeStock <= (stock?.[size.id]?.lowThreshold ?? LOW_STOCK_THRESHOLD);

  // Unit price is the stored price for the selected bottle size (no multipliers).
  const unitPrice = Math.round(priceForSize(product, size.id));

  function handleAdd() {
    if (outOfStock) return;
    const finalPrice = getDiscountedPrice(unitPrice, product.discountPercent);
    addToCart({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      variantId: size.id,
      variantLabel: size.label,
      unitPrice: finalPrice,
      quantity: qty,
      img: product.img,
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <ProductPrice 
            price={unitPrice} 
            discountPercent={product.discountPercent} 
            size="xl" 
          />
          <span className="text-[11px] uppercase tracking-[0.28em] text-black/40">
            {size.label} · {product.concentration}
          </span>
        </div>
        <p className="mt-2 text-xs text-black/50">
          Taxes included.
          {shippingConfig.freeThreshold > 0
            ? ` Free Standard Delivery over ${formatPrice(shippingConfig.freeThreshold)}.`
            : ""}
        </p>
        {outOfStock && (
          <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.28em] text-black/70">
            Out of Stock
          </p>
        )}
        {lowStock && (
          <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.28em] text-[#C6A15B]">
            Only {sizeStock} left
          </p>
        )}
      </div>

      <div>
        <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-black/60">
          Size
        </span>
        <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Size">
          {DEFAULT_SIZES.map((s) => {
            const selected = s.id === size.id;
            return (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setSize(s)}
                className={`min-w-[88px] border px-4 py-3 text-xs uppercase tracking-[0.22em] transition ${
                  selected
                    ? "border-black bg-black text-white"
                    : "border-black/20 text-black hover:border-[#C6A15B] hover:text-[#C6A15B]"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-black/60">
          Quantity
        </span>
        <div className="mt-3 inline-flex items-center border border-black/20">
          <button
            type="button"
            aria-label="Decrease quantity"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            className="flex h-11 w-11 items-center justify-center text-black transition hover:text-[#C6A15B] disabled:opacity-40"
            disabled={qty <= 1}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </button>
          <span
            aria-live="polite"
            className="w-12 text-center text-sm"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {qty}
          </span>
          <button
            type="button"
            aria-label="Increase quantity"
            onClick={() =>
              setQty((q) => Math.min(sizeStock !== null && sizeStock > 0 ? Math.min(9, sizeStock) : 9, q + 1))
            }
            className="flex h-11 w-11 items-center justify-center text-black transition hover:text-[#C6A15B]"
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={handleAdd}
          disabled={outOfStock}
          className="flex-1 bg-black py-4 text-[11px] font-medium uppercase tracking-[0.32em] text-white transition hover:bg-[#C6A15B] disabled:cursor-not-allowed disabled:bg-black/30 disabled:hover:bg-black/30"
        >
          {outOfStock ? (
            "Out of Stock"
          ) : added ? (
            <span className="inline-flex items-center justify-center gap-2">
              <Check className="h-4 w-4" aria-hidden /> Added
            </span>
          ) : (
            "Add to Cart"
          )}
        </button>
        <button
          type="button"
          aria-pressed={wishlisted}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          onClick={() => toggleWishlist(product.id)}
          className={`inline-flex h-14 items-center justify-center gap-2 border px-6 text-[11px] font-medium uppercase tracking-[0.28em] transition ${
            wishlisted
              ? "border-[#C6A15B] text-[#C6A15B]"
              : "border-black text-black hover:border-[#C6A15B] hover:text-[#C6A15B]"
          }`}
        >
          <Heart className={`h-4 w-4 ${wishlisted ? "fill-current" : ""}`} aria-hidden />
          <span className="sm:hidden">Wishlist</span>
        </button>
      </div>
    </div>
  );
}
